import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { brandingService, type Branding } from '@/services/branding.service';

export const BRANDING_QUERY_KEY = ['branding'] as const;

/** How often an open session asks whether the owner changed the photo. */
export const BRANDING_POLL_MS = 60_000;

/** Tabs of the same browser hear about a change at once, without waiting. */
const CHANNEL = 'ramenten-branding';

export function announceBrandingChange() {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(CHANNEL);
  channel.postMessage('changed');
  channel.close();
}

/** Tell the offline cache which photo is current, so it drops the others. */
function tellServiceWorker(branding: Branding) {
  const controller = typeof navigator !== 'undefined' ? navigator.serviceWorker?.controller : null;
  controller?.postMessage({
    type: 'branding-background',
    version: branding.custom ? branding.version : null,
  });
}

/**
 * Draws the company's background photo under the glass interface.
 *
 * The photo is a CSS variable (`--gl-photo`, styles/glass.css) set on <html>,
 * so nothing but the backdrop layer changes. A new photo is applied only once
 * it has loaded: the old one stays on screen until then, never a blank
 * frame. Leaving the signed-in shell (sign-out) returns to the built-in one.
 *
 * How a change reaches a session that is already open: there is no push
 * channel from the server, so an open session asks again every
 * BRANDING_POLL_MS while its tab is visible, and at once when the tab comes
 * back into focus or the network comes back. Other tabs of the same browser
 * are told directly. A hidden tab does not poll.
 */
export function useCompanyBackground() {
  const client = useQueryClient();
  const branding = useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: ({ signal }) => brandingService.get(signal),
    staleTime: 30_000,
    refetchInterval: BRANDING_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });
  const custom = branding.data?.custom ?? false;
  const url = brandingService.backgroundUrl(branding.data);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = () => void client.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
    return () => channel.close();
  }, [client]);

  useEffect(() => {
    if (branding.data) tellServiceWorker(branding.data);
  }, [branding.data]);

  useEffect(() => {
    const root = document.documentElement;
    if (!custom) {
      root.style.removeProperty('--gl-photo');
      return;
    }
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) root.style.setProperty('--gl-photo', `url("${url}")`);
    };
    // A photo that fails to load leaves the current one in place.
    image.src = url;
    return () => {
      cancelled = true;
    };
  }, [custom, url]);

  useEffect(
    () => () => {
      document.documentElement.style.removeProperty('--gl-photo');
    },
    [],
  );
}
